import { Component, OnInit, ViewChild } from '@angular/core';
import { ApexChart, ApexNonAxisChartSeries, ChartComponent, ApexTitleSubtitle } from 'ng-apexcharts';
import * as Highcharts from 'highcharts/highmaps';
import worldMap from "@highcharts/map-collection/custom/world-eckert3-highres.geo.json";
import { SopStorage } from 'app/storage/sop.storage';
import { Logger } from 'app/utilities/logger';
import { ApplicationsService } from 'app/services/applications.service';
import { ApplicationType } from 'app/models/application';
import { TOPIC_GROUPS } from 'app/models/epe-topics';
import { countries } from 'app/shared/store/country-data-store';
import { forEach } from 'lodash';
import { AssociationMongo } from 'app/models/association';
import { EMPTY, catchError, forkJoin, of } from 'rxjs';
import { Alert } from 'app/shared/alerts/simple-message/simple-message.component';
import { AssociationsService } from 'app/services/associations.service';

const SERVICE_TYPES: ApplicationType[] = ['service1', 'service2', 'service3'];

@Component({
  selector: 'statistics',
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.scss', '../sop.component.scss']
})
export class StatisticsComponent implements OnInit {

  loading: boolean = true;
  epes: any[] | null = []
  associations: AssociationMongo[] | null = []
  associationsArray: any[] = []
  projects: any[] | null = []
  projectsCompleted: any[] | null = []
  applications : number = 0;
  fundingProgrammes : string[] = ['Horizon Europe', 'Digital Europe<br/> Programme', 'H2020', 'Other'];
  fundingProgrammesCount : number[] = [0, 0, 0, 0];
  callTopics : string[] = ['Health', 'Resilience', 'Sustainable digitalisation', 'Green transition<br/> in Europe', 'Smart Cities<br/> and Circular Economy<br/> in Buildings'];
  callTopicsCount : number[] = [0, 0, 0, 0, 0];
  serviceDeliveryStatus : string[] = ['Approved', 'Completed', 'In assignment', 'Closed'];
  serviceDeliveryStatusCount : number[] = [0, 0, 0, 0];

  updateFlag = false;

  showAlert: boolean = false
  alertMessage: string = ''
  alertType: Alert = Alert.SUCCESS

  public countries:any = countries;

  fundingProgrammeChart = {
    series: <ApexNonAxisChartSeries> [0 , 0, 0],
    chart: <ApexChart> {
      type: 'donut',
      toolbar: {
        show: true
      }
    },
    labels: this.fundingProgrammes,
    chartTitle: <ApexTitleSubtitle> {
      text: 'Funding Programmes',
      align: 'left',
    }
  };

  callTopicsChart = {
    series: <ApexNonAxisChartSeries> [0 , 0, 0, 0, 0],
    chart: <ApexChart> {
      type: 'pie',
      toolbar: {
        show: true
      }
    },
    labels: this.callTopics,
    chartTitle: <ApexTitleSubtitle> {
      text: 'Call Topics',
      align: 'left',
    }
  };

  serviceDeliveryStatusChart = {
    series: <ApexNonAxisChartSeries> this.serviceDeliveryStatusCount,
    chart: <ApexChart> {
      type: 'donut',
      toolbar: {
        show: true
      }
    },
    labels: this.serviceDeliveryStatus,
    chartTitle: <ApexTitleSubtitle> {
      text: 'Service Delivery Status',
      align: 'left',
    }
  };

  Highcharts: typeof Highcharts = Highcharts;
  chartConstructor = "mapChart";
  chartData = [{ code3: "ABW", z: 105 }, { code3: "AFG", z: 35530 }];

  chartOptions: Highcharts.Options = {
    chart: {
      map: worldMap
    },
    title: {
      text: "<b>Geographical</b> category of projects"
    },
    mapNavigation: {
      enabled: true,
      buttonOptions: {
        alignTo: "spacingBox"
      }
    },
    legend: {
      enabled: true
    },
    colorAxis: {
      min: 0
    },
    series: [
      {
        type: "map",
        name: "Random data",
        states: {
          hover: {
            color: "#BADA55"
          }
        },
        dataLabels: {
          enabled: true,
          format: "{point.name}"
        },
        allAreas: false,
        data: [
        ]
      }
    ]
  };

  p: number = 1;
  itemsPerPage: number = 10;

  

  constructor(
    private cache: SopStorage,
    private logger: Logger,
    public applicationsService: ApplicationsService,
    private associationService: AssociationsService,
  ) { }

  ngOnInit(): void {

    const cachedProjects = this.cache.getProjects()
    const cachedEpes = this.cache.getEpes()

    forkJoin({
      projects: !cachedProjects ? this.applicationsService.getAll({ include_user: true }) : of(null),
      epes: !cachedEpes ? this.applicationsService.getAll({ type: 'epe', include_user: true }) : of(null),
      associations: this.associationService.getAllServices()
    })
    .pipe(
      catchError(
        err => {
          this.addAlert("Association retriving error", Alert.ERROR)
          return EMPTY
        }
      )
    )
    .subscribe(
      then => {
        this.projects = then.projects
          ? then.projects
              .filter((a: any) => SERVICE_TYPES.includes(a.application_type))
              .map((app: any) => ({
                ...(app.form_data || {}),
                ...(app.user_info || {}),
                ...app,
                id: app.user_id ?? app.id,
                application_id: app._id?.['$oid'] ?? app._id,
                sop_status: app.status,
              }))
          : cachedProjects
        this.epes = then.epes
          ? then.epes.map((app: any) => ({
              ...(app.form_data || {}),
              ...(app.user_info || {}),
              ...app,
              id: app.user_id ?? app.id,
              application_id: app._id?.['$oid'] ?? app._id,
              sop_status: app.status,
            }))
          : cachedEpes
        this.associationsArray = (then.associations || [])
          .filter(a => a.association_status === 'completed' || a.meeting === 7)
          .map(association => {
            const pid = association.project_application_id ?? association.project_id
            // application_id first (per-application key); fall back to the
            // account-level id so legacy pre-hsb2 associations still resolve.
            const project = this.projects?.find(p => p.application_id == pid)
                         ?? this.projects?.find(p => p.id == pid)
            const m1 = association.meeting_form_1?.date
            const m5 = association.meeting_form_5?.date
            return {
              ...association,
              project_id: pid,
              epe_id: association.epe_application_id ?? association.epe_id,
              'Project': project?.project_acronym,
              'ActualDuration': this.getActualDuration(m1, m5)
            }
          })
        this.associations = then.associations;
        this.cache.setEpes(this.epes!)
        this.cache.setProjects(this.projects!)
        this.loadCharts()
        this.loading = false
      })


  }

  getActualDuration(meeting_form_1?: string, meeting_form_5?: string){
    if (!meeting_form_1 || !meeting_form_5) return '-'
    const date1 = new Date(meeting_form_5);
    const date2 = new Date(meeting_form_1);
    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) return '-'
    const timeDifference = date1.getTime() - date2.getTime();
    return Math.abs(Math.floor(timeDifference / (24 * 60 * 60 * 1000)))
  }
    

  loadCharts(){
    this.applications = this.projects?.length || 0;
    this.fundingProgrammesCount = [0, 0, 0, 0]
    this.callTopicsCount = [0, 0, 0, 0, 0]
    this.serviceDeliveryStatusCount = [0, 0, 0, 0]
    this.countFundingProgrammes()
    this.fundingProgrammeChart.series = this.fundingProgrammesCount;
    this.countCallTopics()
    this.callTopicsChart.series = this.callTopicsCount;
    this.loadCountries()
    this.countServiceDeliveryStatus()
    this.serviceDeliveryStatusChart.series = this.serviceDeliveryStatusCount;
  }


  countFundingProgrammes(){
    this.projects?.forEach(project => {
      const fp = project.funding_programme
      if(fp == 'HorizonEurope'){
        this.fundingProgrammesCount[0] += 1
      } else if(fp == 'DigitalEuropeProgramme'){
        this.fundingProgrammesCount[1] += 1
      } else if(fp == 'H2020'){
        this.fundingProgrammesCount[2] += 1
      } else if(fp) {
        this.fundingProgrammesCount[3] += 1
      }
    });
  }

  countCallTopics(){
    this.projects?.forEach(project => {
      const topics: string[] = Array.isArray(project.topics) ? project.topics : []
      const groupsSeen = new Set<string>()
      for (const v of topics) {
        for (const g of TOPIC_GROUPS) {
          if (g.subGroups.some(s => s.children.some(c => c.value === v))) {
            groupsSeen.add(g.label)
            break
          }
        }
      }
      if (groupsSeen.has('Health')) this.callTopicsCount[0] += 1
      if (groupsSeen.has('Resilience')) this.callTopicsCount[1] += 1
      if (groupsSeen.has('Sustainable digitalisation')) this.callTopicsCount[2] += 1
      if (groupsSeen.has('Green transition in Europe')) this.callTopicsCount[3] += 1
      if (groupsSeen.has('Smart Cities and Circular Economy in Buildings')) this.callTopicsCount[4] += 1
    });
  }

  countServiceDeliveryStatus(){
    const assoc = this.associations || []
    const approved  = new Set<any>()
    const completed = new Set<any>()
    const closed    = new Set<any>()
    for (const a of assoc) {
      const pid = a.project_application_id ?? a.project_id
      if (pid == null) continue
      if (a.association_status === 'approved')  approved.add(pid)
      if (a.association_status === 'completed') completed.add(pid)
      if (a.association_status === 'closed')    closed.add(pid)
    }
    const eligible = (this.projects || []).filter(p => p.sop_status === 'eligible' || p.status === 'eligible')
    const assigned = new Set<any>([...approved, ...completed, ...closed])
    const inAssignment = eligible.filter(p => !assigned.has(p.application_id) && !assigned.has(p.id)).length

    this.serviceDeliveryStatusCount[0] = approved.size
    this.serviceDeliveryStatusCount[1] = completed.size
    this.serviceDeliveryStatusCount[2] = inAssignment
    this.serviceDeliveryStatusCount[3] = closed.size
  }

  loadCountries(){
    const localCounts: Record<string, number> = {}
    forEach(this.projects, (project: any) => {
      if (!project.country) return
      const match = countries.find((c: any) => c.name === project.country)
      if (match) localCounts[match.code] = (localCounts[match.code] || 0) + 1
    })

    const arrayData: any[] = countries.map((c: any) => [
      c.code.toLowerCase(),
      localCounts[c.code] || 0
    ])

    this.chartOptions.series = [{
      type: "map",
      name: "Projects",
      states: { hover: { color: "#BADA55" } },
      dataLabels: { enabled: true, format: "{point.name}" },
      allAreas: false,
      data: arrayData
    }]

    this.updateFlag = true;
  }

  openNewTab(url: any){
    window.open(url)
  }

  calculateRowIndex(indexOnPage: number): number {
    return (this.p - 1) * this.itemsPerPage + indexOnPage + 1;
  }
  

  addAlert(message: string, type: Alert){
    this.alertMessage = message
    this.alertType = type
    this.showAlert = true
  }

}
